import { useState } from 'react'
import { Alert, Form, Input, Modal, Select, Switch } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { NETWORK_STATUSES, saveCenter, setCenterBranches, type NetworkCenter, type NetworkStatus } from './networkApi'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { branchLabel } from '@/features/branch/branchApi'

interface FormValues {
  name: string
  description?: string
  status: NetworkStatus
  coversAllNas: boolean
  branchIds: string[]
}

export function CenterFormModal({
  open,
  center,
  onClose,
}: {
  open: boolean
  center: NetworkCenter | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { options: branchChoices } = useActiveBranches()

  const submit = () => {
    submitOnValid(form, async (values) => {
      setSaving(true)
      setError(null)
      try {
        const saved = await saveCenter(center?.id ?? null, {
          name: values.name.trim(),
          description: values.description?.trim() ?? '',
          status: values.status,
          coversAllNas: values.coversAllNas,
        })
        if ([...values.branchIds].sort().join() !== [...(center?.branchIds ?? [])].sort().join()) {
          await setCenterBranches(saved.id, values.branchIds)
        }
        void queryClient.invalidateQueries({ queryKey: queryKeys.network.all })
        onClose()
      } catch (err) {
        setError(translateError(err, t))
      } finally {
        setSaving(false)
      }
    })
  }

  return (
    <Modal
      open={open}
      title={center ? t('network.centers.edit') : t('network.centers.create')}
      onCancel={onClose}
      onOk={submit}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) return
        setError(null)
        form.setFieldsValue({
          name: center?.name ?? '',
          description: center?.description ?? '',
          status: center?.status ?? 'ACTIVE',
          coversAllNas: center?.coversAllNas ?? true,
          branchIds: [...(center?.branchIds ?? [])],
        })
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item
          name="name"
          label={t('network.name')}
          rules={[{ required: true, whitespace: true, max: 100, message: t('network.nameRequired') }]}
        >
          <Input autoFocus autoComplete="off" />
        </Form.Item>
        <Form.Item name="description" label={t('network.description')} rules={[{ max: 500 }]}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
        <Form.Item name="status" label={t('network.status.label')}>
          <Select
            disabled={center?.isDefault}
            options={NETWORK_STATUSES.map((s) => ({ value: s, label: t(`network.status.${s}`) }))}
          />
        </Form.Item>
        <Form.Item
          name="coversAllNas"
          label={t('network.centers.coversAllNas')}
          extra={t('network.centers.coversAllNasHint')}
          valuePropName="checked"
        >
          <Switch disabled={center?.isDefault} />
        </Form.Item>
        {branchChoices.length > 0 ? (
          <Form.Item name="branchIds" label={t('branch.labelPlural')} extra={t('branch.centerHint')}>
            <Select<string[]>
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder={t('branch.none')}
              options={branchChoices.map((b) => ({ value: b.id, label: branchLabel(b) }))}
            />
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  )
}
